<template>
  <div class="contact">
    <el-row justify="space-between" type="flex">
      <div class="contact__section-title">Personal details</div>
      <router-link class="edit" :to="{ name: 'step-2', query: { index } }"
        >Edit</router-link
      >
    </el-row>

    <el-row>
      <el-col :span="8">
        <label class="contact__label">Name</label>
        <div
          :class="{ mandatory: !getFullName(contact) }"
          class="contact__value"
        >
          {{ getFullName(contact) | formatDisplayMandatory }}
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Prefer to be addressed as</label>
        <div class="contact__value">{{ contact.nickname | formatDisplay }}</div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Date of birth</label>
        <div
          :class="{ mandatory: !contact.dateOfBirth }"
          class="contact__value"
        >
          {{ contact.dateOfBirth | formatDisplayMandatory }}
        </div>
      </el-col>
    </el-row>

    <el-row>
      <el-col :span="8">
        <label class="contact__label">Email</label>
        <div class="contact__value">{{ contact.email | formatDisplay }}</div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Contact number</label>
        <div class="contact__value">{{ contact.phone | formatDisplay }}</div>
      </el-col>
    </el-row>

    <el-row>
      <el-col :span="8">
        <label class="contact__label">Country of residence</label>
        <div
          :class="{ mandatory: !contact.mainMailingCountry }"
          class="contact__value"
        >
          {{ contact.mainMailingCountry | formatKeyValueMandatory }}
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Nationality</label>
        <div
          :class="{ mandatory: !contact.nationality }"
          class="contact__value"
        >
          {{ contact.nationality | formatKeyValueMandatory }}
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Gender</label>
        <div :class="{ mandatory: !contact.gender }" class="contact__value">
          {{ contact.gender | formatKeyValueMandatory }}
        </div>
      </el-col>
    </el-row>

    <el-row
      v-if="
        !(this.booking.isAmazonCruise && this.isPeruvianContactBooking(contact))
      "
    >
      <el-col :span="8">
        <label class="contact__label">Passport number</label>
        <div
          :class="{ mandatory: !contact.passportNumber }"
          class="contact__value"
        >
          {{ contact.passportNumber | formatDisplayMandatory }}
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Passport issuing country</label>
        <div
          :class="{ mandatory: !contact.passportIssuingCountry }"
          class="contact__value"
        >
          {{ contact.passportIssuingCountry | formatKeyValueMandatory }}
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Expiry date</label>
        <div
          :class="{ mandatory: !contact.passportExpiryDate }"
          class="contact__value"
        >
          {{ contact.passportExpiryDate | formatDisplayMandatory }}
        </div>
      </el-col>
    </el-row>

    <el-row v-if="!booking.isAmazonCruise">
      <el-col :span="8">
        <label class="contact__label">Passport issued date</label>
        <div
          :class="{ mandatory: !contact.passportIssueDate }"
          class="contact__value"
        >
          {{ contact.passportIssueDate | formatDisplayMandatory }}
        </div>
      </el-col>
    </el-row>

    <el-row
      v-if="
        this.booking.isAmazonCruise && this.isPeruvianContactBooking(contact)
      "
    >
      <el-col :span="8">
        <label class="contact__label">Peruvian National ID</label>
        <div
          :class="{ mandatory: !contact.nationalIdNumber }"
          class="contact__value"
        >
          {{ contact.nationalIdNumber | formatDisplayMandatory }}
        </div>
      </el-col>
    </el-row>

    <el-row>
      <el-col :span="8">
        <scope :passport="passportByContactID(contact.contactID)" tag="div">
          <template v-if="passport" slot-scope="{ passport }">
            <label class="contact__label">Passport image</label>
            <div class="contact__value">
              <img
                v-if="passport.file && passport.file.url"
                :src="passport.file.url"
                style="display: block; width: 168px; margin-top: 14px"
              />
              <div v-else class="mandatory" style="margin-top: 10px">
                Please upload your passport image.
              </div>
            </div>
          </template>
        </scope>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Residential address</label>
        <div class="contact__value">
          <div v-if="contact.mainMailingStreet">
            {{ contact.mainMailingStreet }}
          </div>
          <div v-if="contact.mainMailingCity">
            {{ contact.mainMailingCity }}
          </div>
          <div v-if="contact.mainMailingZip || contact.mainMailingState">
            {{ contact.mainMailingZip }}
            {{ contact.mainMailingState | formatKeyValue }}
          </div>
          <div v-if="contact.mainMailingCountry">
            {{ contact.mainMailingCountry | formatKeyValue }}
          </div>
        </div>
      </el-col>
    </el-row>

    <hr v-if="visaRequirementByContactID(contact.contactID)" />
    <scope
      :visaRequirement="visaRequirementByContactID(contact.contactID)"
      tag="div"
    >
      <template v-if="visaRequirement" slot-scope="{ visaRequirement }">
        <el-row justify="space-between" type="flex">
          <h3 style="margin-top: 0; margin-bottom: 8px">
            Have you applied for your cross-border visa?
          </h3>
          <router-link class="edit" :to="{ name: 'step-2', query: { index } }"
            >Edit</router-link
          >
        </el-row>

        <div v-if="visaRequirement.visaType === 'Apply Onboard'">
          No, I will apply on board
        </div>

        <div v-else-if="visaRequirement.visaType === 'Not required'">
          No, I do not need a visa
        </div>

        <div
          v-else-if="
            visaRequirement.visaType === 'Physical Visa (Cross border)'
          "
        >
          Yes, I have
          <img
            v-if="visaRequirement.file && visaRequirement.file.url"
            :src="visaRequirement.file.url"
            style="display: block; width: 168px; margin-top: 14px"
          />
          <div v-else class="mandatory" style="margin-top: 10px">
            Please upload your visa.
          </div>
        </div>

        <div v-else class="mandatory">This is required.</div>
      </template>
    </scope>

    <hr />

    <div>
      <el-row justify="space-between" type="flex">
        <h3 class="label-bold" style="margin-top: 0; margin-bottom: 8px">
          Have you (or your client, if you're an agent) traveled with us before?
        </h3>
        <router-link class="edit" :to="{ name: 'step-2', query: { index } }"
          >Edit</router-link
        >
      </el-row>
      <el-row>
        <div class="answer">
          {{ contact.hasPastGuest ? "Yes, I have" : "No" }}
        </div>
      </el-row>
      <el-row v-if="contact.hasPastGuest">
        <el-col :span="8">
          <label class="contact__label">Previous cruise(s) with us</label>
          <div
            :class="{ mandatory: !contact.pastGuest }"
            class="contact__value"
          >
            {{ contact.pastGuest | formatMultipleDisplayMandatory }}
          </div>
        </el-col>
        <!--        <el-col :span="8">-->
        <!--          <label class="contact__label">Last travel period</label>-->
        <!--          <div class="contact__value">{{-->
        <!--              (contact[getPastTravelPeriodField(contact.pastGuest)]) | formatDisplayLastTravelPeriod-->
        <!--            }}-->
        <!--          </div>-->
        <!--        </el-col>-->
      </el-row>
    </div>

    <hr />

    <el-row justify="space-between" type="flex">
      <div class="contact__section-title">Emergency contact</div>
      <router-link class="edit" :to="{ name: 'step-2', query: { index } }"
        >Edit</router-link
      >
    </el-row>

    <el-row>
      <el-col :span="8">
        <label class="contact__label">Name</label>
        <div
          :class="{ mandatory: !getEmergencyFullName(contact) }"
          class="contact__value"
        >
          {{ getEmergencyFullName(contact) | formatDisplayMandatory }}
        </div>
        <div
          v-if="
            !contact.emergencyContactFirstName &&
            contact.emergencyContactLastName
          "
          class="contact__value mandatory"
        >
          First name is required.
        </div>
        <div
          v-if="
            !contact.emergencyContactLastName &&
            contact.emergencyContactFirstName
          "
          class="contact__value mandatory"
        >
          Last name is required.
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Contact</label>
        <div
          :class="{ mandatory: !contact.emergencyContactPhone }"
          class="contact__value"
        >
          {{ contact.emergencyContactPhone | formatDisplayMandatory }}
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Relationship</label>
        <div
          :class="{ mandatory: !contact.emergencyContactRelation }"
          class="contact__value"
        >
          {{ contact.emergencyContactRelation | formatDisplayMandatory }}
        </div>
      </el-col>
    </el-row>

    <div v-if="isAquaBlu()">
      <hr />
      <el-row justify="space-between" type="flex">
        <div class="contact__section-title">
          Release of Liability and Assumption of Risk
        </div>
        <router-link class="edit" :to="{ name: 'step-2', query: { index } }"
          >Edit</router-link
        >
      </el-row>
      <el-row>
        <div
          :class="{ mandatory: !contact.HasSignedReleaseOfLiability }"
          class="contact__value"
        >
          {{ contact.HasSignedReleaseOfLiability | formatSignedROL }}
        </div>
      </el-row>
    </div>

    <hr />

    <el-row justify="space-between" type="flex">
      <div class="contact__section-title">Dietary and special requests</div>
      <router-link class="edit" :to="{ name: 'step-3', query: { index } }"
        >Edit</router-link
      >
    </el-row>

    <el-row>
      <!--      <el-col :span="8" v-if="canShowDietaryPreferenceField()">-->
      <!--        <label class="contact__label">Dietary restrictions</label>-->
      <!--        <div-->
      <!--          :class="{ mandatory: !contact.dietaryPreferences }"-->
      <!--          class="contact__value"-->
      <!--        >-->
      <!--          {{ contact.dietaryPreferences | formatMultipleDisplayMandatory }}-->
      <!--        </div>-->
      <!--      </el-col>-->
      <el-col :span="8">
        <label class="contact__label">Dietary Allergies</label>
        <div
          :class="{ mandatory: !contact.dietaryAllergies }"
          class="contact__value"
        >
          {{ contact.dietaryAllergies | formatMultipleDisplayMandatory }}
        </div>
      </el-col>
      <el-col :span="8">
        <label class="contact__label">Medical and health notes</label>
        <div
          :class="{ mandatory: !contact.medicalNotes }"
          class="contact__value"
        >
          {{ contact.medicalNotes | formatDisplayMandatory }}
        </div>
      </el-col>
    </el-row>
    <el-row>
      <el-col :span="8">
        <label class="contact__label">Dietary Preferences note</label>
        <div class="contact__value">
          {{ contact.dietaryPreferenceNotes | formatDisplay }}
        </div>
      </el-col>
      <!--      <el-col :span="8">-->
      <!--        <label class="contact__label">Additional note</label>-->
      <!--        <div class="contact__value">-->
      <!--          {{ contact.dietaryAllergiesNotes | formatDisplay }}-->
      <!--        </div>-->
      <!--      </el-col>-->
    </el-row>
    <el-row>
      <el-col :span="8">
        <label class="contact__label">Special request</label>
        <div class="contact__value">
          {{ booking.checkInRemarks | formatDisplay }}
        </div>
      </el-col>
    </el-row>

    <hr />

    <template v-if="hasDiving()">
      <el-row justify="space-between" type="flex">
        <div class="contact__section-title">Diving information</div>
        <router-link class="edit" :to="{ name: 'step-3b', query: { index } }"
          >Edit</router-link
        >
      </el-row>
      <template
        v-if="contactBooking.planningToDive === true && canDive(contact)"
      >
        <el-row>
          <el-col :span="8">
            <label class="contact__label">Wetsuit size</label>
            <div
              :class="{ mandatory: !contactBooking.divingWetsuit }"
              class="contact__value"
            >
              {{ contactBooking.divingWetsuit | formatDisplayMandatory }}
            </div>
          </el-col>
          <el-col :span="8">
            <label class="contact__label">Fin size</label>
            <div
              :class="{ mandatory: !contactBooking.divingFins }"
              class="contact__value"
            >
              {{ contactBooking.divingFins | formatDisplayMandatory }}
            </div>
          </el-col>
          <el-col :span="8">
            <label class="contact__label">Diving experience</label>
            <div
              :class="{ mandatory: !contactBooking.divingExperienceLevel }"
              class="contact__value"
            >
              {{
                contactBooking.divingExperienceLevel | formatDisplayMandatory
              }}
            </div>
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="8">
            <label class="contact__label">Date of last dive</label>
            <div
              :class="{ mandatory: !contactBooking.dateOfLastDive }"
              class="contact__value"
            >
              {{ contactBooking.dateOfLastDive | formatDisplayMandatory }}
            </div>
          </el-col>
          <el-col :span="8">
            <label class="contact__label">Diving Certification Number</label>
            <div
              :class="{ mandatory: !contactBooking.divingCertificationNumber }"
              class="contact__value"
            >
              {{
                contactBooking.divingCertificationNumber
                  | formatDisplayMandatory
              }}
            </div>
          </el-col>
          <el-col v-if="contactBooking.divingAssociation !== 'Other'" :span="8">
            <label class="contact__label">Diving Association</label>
            <div
              :class="{ mandatory: !contactBooking.divingAssociation }"
              class="contact__value"
            >
              {{ contactBooking.divingAssociation | formatDisplayMandatory }}
            </div>
          </el-col>
          <el-col v-if="contactBooking.divingAssociation === 'Other'" :span="8">
            <label class="contact__label">Diving Association (Other)</label>
            <div
              :class="{ mandatory: !contactBooking.divingAssociationOther }"
              class="contact__value"
            >
              {{
                contactBooking.divingAssociationOther | formatDisplayMandatory
              }}
            </div>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="8">
            <label class="contact__label">PADI Release of Liability</label>
            <div
              :class="{
                mandatory: !contactBooking.HasSignedPADIReleaseOfLiability,
              }"
              class="contact__value"
            >
              {{
                contactBooking.HasSignedPADIReleaseOfLiability | formatSignedROL
              }}
            </div>
          </el-col>
        </el-row>
        <!--        <el-row>-->
        <!--          <el-col :span="8">-->
        <!--            <label class="contact__label">Diving insurance number</label>-->
        <!--            <div class="contact__value" :class="{ 'mandatory': !contactBooking.divingInsuranceNumber }">{{ contactBooking.divingInsuranceNumber | formatDisplayMandatory }}</div>-->
        <!--          </el-col>-->
        <!--        </el-row>-->
        <div class="note">
          Reminder: Please bring your diving certificate (PADI or equivalent) on
          board.
        </div>
      </template>
      <template v-else>
        <el-row>
          <el-col :span="8">
            <label class="contact__label">Are you diving?</label>
            <div class="contact__value">No</div>
          </el-col>
        </el-row>
      </template>
      <hr />
    </template>

    <template v-if="hasWetsuit()">
      <el-row justify="space-between" type="flex">
        <div class="contact__section-title">Wetsuit information</div>
        <router-link class="edit" :to="{ name: 'step-3c', query: { index } }"
          >Edit</router-link
        >
      </el-row>
      <template v-if="contactBooking.needAWetsuit === true">
        <el-row>
          <el-col :span="8">
            <label class="contact__label">Wetsuit size</label>
            <div
              :class="{ mandatory: !contactBooking.wetsuit }"
              class="contact__value"
            >
              {{ contactBooking.wetsuit | formatDisplayMandatory }}
            </div>
          </el-col>
          <el-col v-if="contactBooking.wetsuit === 'Other'" :span="8">
            <label class="contact__label">Wetsuit size (Other)</label>
            <div
              :class="{ mandatory: !contactBooking.wetsuitOther }"
              class="contact__value"
            >
              {{ contactBooking.wetsuitOther | formatDisplayMandatory }}
            </div>
          </el-col>
        </el-row>
        <hr />
      </template>
      <template v-else>
        <el-row>
          <el-col :span="8">
            <label class="contact__label">Do you need a wetsuit?</label>
            <div class="contact__value">No</div>
          </el-col>
        </el-row>
      </template>
    </template>

    <el-row justify="space-between" type="flex">
      <div class="contact__section-title">
        {{
          booking.isGalapagosCruise
            ? "Arrival to Galapagos"
            : "Pre-cruise information"
        }}
      </div>
      <router-link class="edit" :to="{ name: 'step-4', query: { index } }"
        >Edit</router-link
      >
    </el-row>
    <!--<el-row>-->
    <!--<el-col :span="8">-->
    <!--<label class="contact__label">Flying from</label>-->
    <!--<div class="contact__value">{{ }}</div>-->
    <!--</el-col>-->
    <!--<el-col :span="8">-->
    <!--<label class="contact__label">Date</label>-->
    <!--<div class="contact__value">{{ }}</div>-->
    <!--</el-col>-->
    <!--<el-col :span="8">-->
    <!--<label class="contact__label">Time</label>-->
    <!--<div class="contact__value">{{ }}</div>-->
    <!--</el-col>-->
    <!--</el-row>-->

    <scope
      :preArrangement="
        preArrangementByContactIDAndCategory(contact.contactID, 'Flight')
      "
      tag="el-row"
    >
      <template
        v-if="
          preArrangement &&
          preArrangement.flightBookedByAqua &&
          booking.facilityCode === 'AQM'
        "
        slot-scope="{ preArrangement }"
      >
        <div class="contact__value">
          Aqua expeditions has partnered with LATAM Airlines to offer
          preferential rates on flights from Quito or Guayaquil which arrive on
          the day of Aqua Mare’s departure. Our Guest Relations Coordinator will
          reach out to coordinate the details of your flights.
        </div>
        <div class="note">
          If you want to make any changes or arrange your own flights, please
          reach out to
          <a href="mailto:reservations@aquaexpeditions.com"
            >reservations@aquaexpeditions.com</a
          >
        </div>
      </template>
    </scope>

    <scope
      :preArrangement="
        preArrangementByContactIDAndCategory(contact.contactID, 'Flight')
      "
      tag="el-row"
    >
      <template
        v-if="
          preArrangement &&
          (!preArrangement.notTakingAFlight ||
            preArrangement.takingRecommendedFlight) &&
          !preArrangement.flightBookedByAqua
        "
        slot-scope="{ preArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Flight number</label>
          <div
            :class="{ mandatory: !preArrangement.flightNumber }"
            class="contact__value"
          >
            {{ preArrangement.flightNumber | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Date</label>
          <div
            :class="{ mandatory: !preArrangement.arrivalDate }"
            class="contact__value"
          >
            {{ preArrangement.arrivalDate | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Time</label>
          <div
            :class="{ mandatory: !preArrangement.arrivalTime }"
            class="contact__value"
          >
            {{ preArrangement.arrivalTime | formatDisplayMandatory }}
          </div>
        </el-col>
      </template>
    </scope>

    <scope
      v-if="booking.isGalapagosCruise"
      :preArrangement="
        preArrangementByContactIDAndCategory(contact.contactID, 'Flight')
      "
      tag="el-row"
    >
      <template
        v-if="preArrangement && !preArrangement.flightBookedByAqua"
        slot-scope="{ preArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Embarking from</label>
          <div
            :class="{ mandatory: !preArrangement.galapagosRouteInfo }"
            class="contact__value"
          >
            {{ preArrangement.galapagosRouteInfo | formatGalapagosRouteInfo }}
          </div>
        </el-col>
      </template>
    </scope>

    <scope
      :preArrangement="
        preArrangementByContactIDAndCategory(
          contact.contactID,
          'Location to port',
        )
      "
      :preArrangementAccommodation="
        preArrangementByContactIDAndCategory(contact.contactID, 'Accommodation')
      "
      tag="el-row"
    >
      <template
        v-if="preArrangement"
        slot-scope="{ preArrangement, preArrangementAccommodation }"
      >
        <el-col
          v-if="
            preArrangementByContactIDAndCategory(contact.contactID, 'Flight') &&
            !preArrangementByContactIDAndCategory(contact.contactID, 'Flight')
              .takingRecommendedFlight &&
            preArrangementAccommodation.arrivePriorToCruise
          "
          :span="8"
        >
          <label class="contact__label">Accommodation</label>
          <div
            :class="{
              mandatory:
                preArrangementAccommodation.arrivePriorToCruise &&
                !preArrangementAccommodation.accommodationName,
            }"
            class="contact__value"
          >
            {{
              (preArrangementAccommodation &&
                preArrangementAccommodation.accommodationName)
                | formatDisplayMandatory
            }}
          </div>
        </el-col>
        <el-col v-if="!booking.isGalapagosCruise" :span="8">
          <label class="contact__label">Transportation</label>
          <div
            :class="{ mandatory: !preArrangement.typeOfArrangement }"
            class="contact__value"
          >
            {{ preArrangement.typeOfArrangement | formatTypeOfArrangement }}
          </div>
        </el-col>
      </template>
    </scope>
    <scope
      :preArrangement="
        preArrangementByContactIDAndCategory(
          contact.contactID,
          'Location to port',
        )
      "
      tag="el-row"
    >
      <template
        v-if="
          preArrangement &&
          booking.isAmazonCruise &&
          contact.mainMailingCountry !== 'PE - Peru'
        "
        slot-scope="{ preArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Date of entry to Peru</label>
          <div
            :class="{ mandatory: !preArrangement.dateOfEntryToPeru }"
            class="contact__value"
          >
            {{ preArrangement.dateOfEntryToPeru | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">From</label>
          <div
            :class="{ mandatory: !preArrangement.entryToPeruFrom }"
            class="contact__value"
          >
            {{ preArrangement.entryToPeruFrom | formatDisplayMandatory }}
          </div>
        </el-col>
      </template>
    </scope>

    <scope
      :preArrangement="
        preArrangementByContactIDAndCategory(
          contact.contactID,
          'Location to port',
        )
      "
      tag="el-row"
    >
      <template
        v-if="
          preArrangement && preArrangement.typeOfArrangement === 'By 3rd party'
        "
        slot-scope="{ preArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Local handler's name</label>
          <div
            :class="{ mandatory: !preArrangement.localHandler }"
            class="contact__value"
          >
            {{ preArrangement.localHandler | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Driver name</label>
          <div
            :class="{ mandatory: !preArrangement.nameOfPerson }"
            class="contact__value"
          >
            {{ preArrangement.nameOfPerson | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Driver contact</label>
          <div
            :class="{ mandatory: !preArrangement.contactNo }"
            class="contact__value"
          >
            {{ preArrangement.contactNo | formatDisplayMandatory }}
          </div>
        </el-col>
      </template>
    </scope>
    <el-row>
      <div v-if="booking.isGalapagosCruise" class="note" style="margin-top: 0">
        All transfers to the port will be arranged by Aqua Expeditions and are
        shared.
      </div>
    </el-row>
    <el-row>
      <div class="contact__section-title">
        {{
          booking.isGalapagosCruise
            ? "Departure from Galapagos"
            : "Post-cruise information"
        }}
      </div>
    </el-row>

    <scope
      :postArrangement="
        postArrangementByContactIDAndCategory(contact.contactID, 'Flight')
      "
      tag="el-row"
    >
      <template
        v-if="
          postArrangement &&
          postArrangement.flightBookedByAqua &&
          booking.facilityCode === 'AQM'
        "
        slot-scope="{ postArrangement }"
      >
        <div class="contact__value">
          Aqua expeditions has partnered with LATAM Airlines to offer
          preferential rates on flights from Quito or Guayaquil which arrive on
          the day of Aqua Mare’s departure. Our Guest Relations Coordinator will
          reach out to coordinate the details of your flights.
        </div>
        <div class="note">
          If you want to make any changes or arrange your own flights, please
          reach out to
          <a href="mailto:reservations@aquaexpeditions.com"
            >reservations@aquaexpeditions.com</a
          >
        </div>
      </template>
    </scope>

    <scope
      :postArrangement="
        postArrangementByContactIDAndCategory(contact.contactID, 'Flight')
      "
      tag="el-row"
    >
      <template
        v-if="
          postArrangement &&
          (postArrangementByContactIDAndCategory(
            contact.contactID,
            'Port to location',
          ).postArrangementAction === 'airport' ||
            booking.isGalapagosCruise) &&
          !postArrangement.flightBookedByAqua
        "
        slot-scope="{ postArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Flight number</label>
          <div
            :class="{ mandatory: !postArrangement.flightNumber }"
            class="contact__value"
          >
            {{ postArrangement.flightNumber | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Date</label>
          <div
            :class="{ mandatory: !postArrangement.departureDate }"
            class="contact__value"
          >
            {{ postArrangement.departureDate | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Time</label>
          <div
            :class="{ mandatory: !postArrangement.departureTime }"
            class="contact__value"
          >
            {{ postArrangement.departureTime | formatDisplayMandatory }}
          </div>
        </el-col>
      </template>
    </scope>

    <scope
      v-if="booking.isGalapagosCruise"
      :postArrangement="
        postArrangementByContactIDAndCategory(contact.contactID, 'Flight')
      "
      tag="el-row"
    >
      <template
        v-if="postArrangement && !postArrangement.flightBookedByAqua"
        slot-scope="{ postArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Disembarking from</label>
          <div
            :class="{ mandatory: !postArrangement.galapagosRouteInfo }"
            class="contact__value"
          >
            {{ postArrangement.galapagosRouteInfo | formatGalapagosRouteInfo }}
          </div>
        </el-col>
      </template>
    </scope>

    <scope
      :postArrangement="
        postArrangementByContactIDAndCategory(contact.contactID, 'Flight')
      "
      tag="el-row"
    >
      <template
        v-if="postArrangement && postArrangement.takingRecommendedFlight"
        slot-scope="{ postArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Flight reference number</label>
          <div
            :class="{ mandatory: !postArrangement.referenceNumber }"
            class="contact__value"
          >
            {{ postArrangement.referenceNumber | formatDisplayMandatory }}
          </div>
        </el-col>
      </template>
    </scope>

    <scope
      v-if="!booking.isGalapagosCruise"
      :postArrangement="
        postArrangementByContactIDAndCategory(
          contact.contactID,
          'Port to location',
        )
      "
      :postArrangementAccommodation="
        postArrangementByContactIDAndCategory(
          contact.contactID,
          'Accommodation',
        )
      "
      tag="el-row"
    >
      <template
        v-if="postArrangement"
        slot-scope="{ postArrangement, postArrangementAccommodation }"
      >
        <el-col
          v-if="postArrangement.postArrangementAction === 'hotel'"
          :span="8"
        >
          <label class="contact__label">Accommodation</label>
          <div
            :class="{
              mandatory: !postArrangementAccommodation.accommodationName,
            }"
            class="contact__value"
          >
            {{
              (postArrangementAccommodation &&
                postArrangementAccommodation.accommodationName)
                | formatDisplayMandatory
            }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Transportation</label>
          <div
            :class="{ mandatory: !postArrangement.typeOfArrangement }"
            class="contact__value"
          >
            {{ postArrangement.typeOfArrangement | formatTypeOfArrangement }}
          </div>
        </el-col>
      </template>
    </scope>

    <scope
      :postArrangement="
        postArrangementByContactIDAndCategory(
          contact.contactID,
          'Port to location',
        )
      "
      tag="el-row"
    >
      <template
        v-if="
          postArrangement &&
          postArrangement.typeOfArrangement === 'By 3rd party'
        "
        slot-scope="{ postArrangement }"
      >
        <el-col :span="8">
          <label class="contact__label">Local handler's name</label>
          <div
            :class="{ mandatory: !postArrangement.localHandler }"
            class="contact__value"
          >
            {{ postArrangement.localHandler | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Driver name</label>
          <div
            :class="{ mandatory: !postArrangement.nameOfPerson }"
            class="contact__value"
          >
            {{ postArrangement.nameOfPerson | formatDisplayMandatory }}
          </div>
        </el-col>
        <el-col :span="8">
          <label class="contact__label">Driver contact</label>
          <div
            :class="{ mandatory: !postArrangement.contactNo }"
            class="contact__value"
          >
            {{ postArrangement.contactNo | formatDisplayMandatory }}
          </div>
        </el-col>
      </template>
    </scope>
    <el-row>
      <div v-if="booking.isGalapagosCruise" class="note" style="margin-top: 0">
        All transfers to the port will be arranged by Aqua Expeditions and are
        shared.
      </div>
    </el-row>

    <hr v-if="!isLastContact" />

    <el-row
      v-if="!isLastContact"
      justify="end"
      style="margin-bottom: 0"
      type="flex"
    >
      <a class="link--nextGuest" @click="onClickNextGuest(contact)"
        >Next Guest</a
      >
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import types from "../store/types";
import contactMixin from "../mixins/contact";
import {
  FACILITY_CODES,
  GALAPAGOS_FLIGHT_DESTINATION,
  TYPE_OF_ARRANGEMENTS,
  TYPE_OF_ROUTE_FROM_GALAPAGOS,
  TYPE_OF_ROUTE_TO_GALAPAGOS,
} from "../constants";
import { timeTrack } from "../mixins/timeTrack";

const typeOfArrangementMap = TYPE_OF_ARRANGEMENTS.reduce(
  (accumulate, current) => {
    accumulate[current.value] = current.label;
    return accumulate;
  },
  {},
);

const typeOfRouteToGalapagos = TYPE_OF_ROUTE_TO_GALAPAGOS.reduce(
  (accumulate, current) => {
    accumulate[current.value] = current.label;
    return accumulate;
  },
  {},
);

const typeOfRouteFromGalapagos = TYPE_OF_ROUTE_FROM_GALAPAGOS.reduce(
  (accumulate, current) => {
    accumulate[current.value] = current.label;
    return accumulate;
  },
  {},
);

export default {
  props: {
    contact: Object,
    contactBooking: Object,
    index: Number,
    isLastContact: Boolean,
    onClickNextGuest: Function,
  },
  methods: {
    canShowDietaryPreferenceField: function () {
      return (
        !this.booking.isAmazonCruise &&
        this.booking.facilityCode !== FACILITY_CODES.AquaBlu
      );
    },
    isPeruvianContactBooking(contact) {
      return (
        contact.nationality === "PE - Peru" &&
        contact.mainMailingCountry === "PE - Peru"
      );
    },
    getGalapagosFlightDestination: function (from) {
      return GALAPAGOS_FLIGHT_DESTINATION[from];
    },
    isAquaBlu: function () {
      if (!this.booking || !this.booking.facilityCode) {
        return false;
      }
      return this.booking.facilityCode === FACILITY_CODES.AquaBlu;
    },
  },
  mixins: [contactMixin, timeTrack],
  data() {
    return {
      typeOfArrangementMap,
    };
  },
  computed: {
    ...mapGetters([
      types.getters.cabinConfigurations,
      types.getters.contacts,
      types.getters.booking,
      types.getters.passports,
      types.getters.passportByContactID,
      types.getters.visaRequirements,
      types.getters.preArrangements,
      types.getters.postArrangements,
      types.getters.visaRequirementByContactID,
      types.getters.preArrangementByContactIDAndCategory,
      types.getters.postArrangementByContactIDAndCategory,
    ]),
  },
  filters: {
    formatDisplay: (value) => value || "-",
    formatDisplayMandatory: (value) => value || "This is required.",
    formatMultipleDisplayMandatory: (value) => {
      if (value) {
        if (Array.isArray(value)) {
          return value[0] === "" ? "This is required." : value.join(", ");
        }
        return value;
      }
      return "This is required.";
    },
    formatKeyValue: (keyValue) => {
      // keyValue is in the format "key - value"
      // this function convert keyValue to human-friendly display by showing value only or '-' if keyValue is null
      return keyValue && keyValue.split(" - ").length > 1
        ? keyValue.split(" - ")[1]
        : "-";
    },
    formatKeyValueMandatory: (keyValue) => {
      // keyValue is in the format "key - value"
      // this function convert keyValue to human-friendly display by showing value only or '-' if keyValue is null
      return keyValue && keyValue.split(" - ").length > 1
        ? keyValue.split(" - ")[1]
        : "This is required.";
    },
    formatTypeOfArrangement: (value) =>
      typeOfArrangementMap[value] || "This is required.",
    formatGalapagosRouteInfo: (value) =>
      typeOfRouteToGalapagos[value] ||
      typeOfRouteFromGalapagos[value] ||
      "This is required.",
    formatDisplayLastTravelPeriod: (value) => {
      if (!value) {
        return "-";
      }

      // Supposedly in the format 'MM YYYY'
      const monthYear = value.split(" ");

      // unknown format, just return original value
      if (monthYear.length !== 2) {
        return value;
      }

      const [month, year] = monthYear;
      const monthMap = {
        "01": "Jan",
        "02": "Feb",
        "03": "Mar",
        "04": "Apr",
        "05": "May",
        "06": "Jun",
        "07": "Jul",
        "08": "Aug",
        "09": "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec",
      };

      return `${monthMap[month] || month} ${year}`;
    },
    formatSignedROL: (value) => {
      return value ? "Signed" : "This is required.";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../scss/color";
@import "../scss/typography";

a {
  @include typo(18);
}

.contact {
  padding: 40px;
  background-color: #fff;

  .el-row {
    margin-bottom: 24px;

    &:empty {
      margin-bottom: 0;
    }
  }

  &__section-title {
    @include global-paragraph-x-large-bold;
    color: $aqua-grey-100;
  }

  &__label {
    @include global-paragraph-small-regular;
    color: $aqua-grey-90;
  }

  &__value {
    padding-right: 8px;
    @include global-paragraph-medium-bold;
    color: $aqua-grey-100;
  }
}

.link--nextGuest:hover {
  cursor: pointer;
}

.edit {
  @include global-paragraph-medium-regular;
}

.label-bold {
  @include global-paragraph-medium-bold;
  color: $aqua-grey-100;
}

.answer {
  @include global-paragraph-medium-regular;
  color: $aqua-grey-100;
}

.mandatory {
  color: #f56c6c;
  @include global-paragraph-medium-bold;
}
</style>



// WEBPACK FOOTER //
// src/components/ContactSummary.vue